/**
 * AddonServicePopup - Shows after a successful booking to offer add-on services
 * Supports MULTIPLE addon alternatives. Customer picks one.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Gift, Clock, Loader2, Check, Info, ExternalLink } from 'lucide-react';
import axios from 'axios';
import ConsentSignSection from './ConsentSignSection';
import { et, etp, etTime, EMBED_LANG } from '../../i18n/embedLang';
import { getPortalToken } from '@/utils/tokenUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AddonServicePopup = ({
  open,
  onClose,
  mainBooking,  // { service_id, trainer_id, date, time, customer_id }
  onAddonBooked,
  // Public-iFrame-Modus (07/2026): appointment_id der Public-Buchung.
  // Wenn gesetzt, werden die auth-freien /public/-Endpoints genutzt.
  publicAppointmentId,
}) => {
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [booking, setBooking] = useState(false);
  // Einverständnis-Pflicht (Kryo/EMS-Add-ons): Formular + Sign-Payload
  const [consentForm, setConsentForm] = useState(null);
  const [consentPayload, setConsentPayload] = useState(null);

  const checkAvailability = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (publicAppointmentId) {
        response = await axios.get(`${API}/public/addon-services/check-availability`, {
          params: { appointment_id: publicAppointmentId },
        });
      } else {
        const token = getPortalToken();
        response = await axios.get(`${API}/portal/addon-services/check-availability`, {
          params: {
            main_service_id: mainBooking.service_id,
            trainer_id: mainBooking.trainer_id,
            booking_date: mainBooking.date,
            booking_time: mainBooking.time,
            // Iter 290: Studio-Filter — nur Add-ons aus diesem Standort
            ...(mainBooking.studio_id ? { studio_id: mainBooking.studio_id } : {}),
          },
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setAvailability(response.data);
      setSelectedIdx(0);
      // Silent close NUR wenn gar kein Add-on konfiguriert ist. Wenn eines
      // konfiguriert, aber blockiert ist → deutliches Info-Popup (statt
      // leicht übersehbarem Toast, User-Wunsch 07/2026).
      // Public-iFrame: Gäste bekommen KEIN "nicht buchbar"-Popup → silent close.
      if (!response.data?.available && (publicAppointmentId || !response.data?.configured)) {
        setTimeout(() => onClose && onClose(), 50);
      }
    } catch (error) {
      console.error('Check addon availability error:', error);
      setAvailability({ available: false });
      setTimeout(() => onClose && onClose(), 50);
    } finally {
      setLoading(false);
    }
  }, [mainBooking, publicAppointmentId, onClose]);

  useEffect(() => {
    if (open && (publicAppointmentId || mainBooking)) checkAvailability();
  }, [open, mainBooking, publicAppointmentId, checkAvailability]);

  // Normalize to always work with the `addons` list (backward compat) + DEDUPE by addon id
  const addons = React.useMemo(() => {
    if (!availability?.available) return [];
    let raw = [];
    if (Array.isArray(availability.addons) && availability.addons.length > 0) {
      raw = availability.addons;
    } else if (availability.addon_service) {
      // Legacy single-addon shape
      raw = [{
        addon_service: availability.addon_service,
        combo_price: availability.combo_price,
        savings: availability.savings,
        addon_start_time: availability.addon_start_time,
        addon_end_time: availability.addon_end_time,
        trainer_id: availability.trainer_id,
        trainer_name: availability.trainer_name,
      }];
    }
    // Dedupe by addon_service.id (keep cheapest combo_price)
    const byId = new Map();
    for (const a of raw) {
      const id = a?.addon_service?.id;
      if (!id) continue;
      const existing = byId.get(id);
      if (!existing || (a.combo_price ?? 0) < (existing.combo_price ?? 0)) {
        byId.set(id, a);
      }
    }
    return Array.from(byId.values());
  }, [availability]);

  // Einverständnis-Formular für das gewählte Add-on laden (falls nötig)
  const selectedConsentFormId = addons[selectedIdx]?.consent_form_id || null;
  useEffect(() => {
    setConsentPayload(null);
    if (selectedConsentFormId) {
      axios.get(`${API}/public/consent-forms/${selectedConsentFormId}?lang=${EMBED_LANG}`)
        .then(r => setConsentForm(r.data))
        .catch(() => setConsentForm(null));
    } else {
      setConsentForm(null);
    }
  }, [selectedConsentFormId]);

  const bookSelectedAddon = async () => {
    const chosen = addons[selectedIdx];
    if (!chosen) return;
    if (chosen.consent_form_id && consentForm && !consentPayload) {
      toast.error(et('Bitte fülle die Einverständnis-Erklärung aus und unterschreibe'));
      return;
    }
    setBooking(true);
    try {
      let response;
      if (publicAppointmentId) {
        response = await axios.post(`${API}/public/book-addon`, {
          appointment_id: publicAppointmentId,
          addon_service_id: chosen.addon_service.id,
          consent: consentPayload || null,
        });
      } else {
        const token = getPortalToken();
        // Portal-Modus: fehlende Unterschrift zuerst speichern, dann buchen
        if (chosen.consent_form_id && consentPayload) {
          try {
            await axios.post(`${API}/portal/consents/sign`, consentPayload, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (e) {
            const det = e.response?.data?.detail;
            if (!(typeof det === 'string' && det.includes('Bereits unterschrieben'))) throw e;
          }
        }
        response = await axios.post(`${API}/portal/booking/book`, {
          service_id: chosen.addon_service.id,
          trainer_id: chosen.trainer_id,
          date: mainBooking.date,
          time: chosen.addon_start_time,
          is_addon: true,
          main_appointment_time: mainBooking.time,
          parent_appointment_id: mainBooking.appointment_id,
          addon_price: chosen.combo_price,
          // Iter 351m (Parität mit Mobile App, Iter 336.g): Studio der
          // Hauptbuchung mitgeben, damit das Add-on im selben Studio landet.
          ...(mainBooking.studio_id ? { studio_id: mainBooking.studio_id } : {}),
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      toast.success(et('Zusatzleistung erfolgreich gebucht!'));
      onAddonBooked?.(response.data, chosen);
      onClose();
    } catch (error) {
      console.error('Book addon error:', error);
      const det = error.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : (det?.message || 'Buchung fehlgeschlagen'));
    } finally {
      setBooking(false);
    }
  };

  if (!open) return null;
  const showUnavailable = !loading && availability && !availability.available && availability.configured && !publicAppointmentId;
  if (!loading && !showUnavailable && (!availability?.available || addons.length === 0)) return null;

  // Deutliches Popup wenn Add-on konfiguriert, aber nicht buchbar
  if (showUnavailable) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="addon-unavailable-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-yellow-500" />
              {et('Keine Zusatzleistung buchbar')}
            </DialogTitle>
            <DialogDescription>
              {et('Zu deinem Termin kann aktuell keine Zusatzleistung angeboten werden.')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p
              className="text-sm bg-yellow-500/10 border border-yellow-500/30 text-yellow-600 rounded-lg p-3"
              data-testid="addon-unavailable-reason"
            >
              {availability.reason || et('Der Trainer ist zu dieser Zeit belegt.')}
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={onClose}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              data-testid="addon-unavailable-ok-btn"
            >
              {et('Verstanden')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="addon-service-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-yellow-500" />
            {et('Zusatzleistung hinzufügen?')}
          </DialogTitle>
          <DialogDescription>
            {addons.length > 1
              ? etp('Wählen Sie eine von {0} passenden Leistungen', [addons.length])
              : et('Optimieren Sie Ihr Training mit einer ergänzenden Leistung')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-white/60" />
            </div>
          ) : (
            <div className="space-y-3">
              {addons.map((a, idx) => {
                const isSelected = idx === selectedIdx;
                const svc = a.addon_service || {};
                const recommended = a.recommended === true;
                return (
                  <button
                    key={`addon-opt-${idx}`}
                    type="button"
                    onClick={() => setSelectedIdx(idx)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all relative ${
                      isSelected
                        ? 'border-yellow-400 bg-yellow-50 ring-2 ring-yellow-300'
                        : 'border-gray-200 bg-white hover:border-yellow-200'
                    }`}
                    data-testid={`addon-option-${idx}`}
                  >
                    {recommended && (
                      <div
                        className="absolute -top-2 -right-2 bg-yellow-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md flex items-center gap-1"
                        data-testid={`addon-recommended-badge-${idx}`}
                      >
                        {et('Empfohlen')}
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-yellow-500 bg-yellow-500' : 'border-gray-300'}`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </span>
                          <h3 className="font-semibold text-base truncate">{svc.name}</h3>
                          {/* Hinweis-Icon mit Kurzbeschreibung — User-Wunsch
                              07.05.2026: Statt "Schon X× gebucht" zeigen wir
                              jetzt einen dezenten "i"-Tooltip mit der vom
                              Admin gepflegten Description. */}
                          {svc.description && (
                            <span
                              role="button"
                              tabIndex={0}
                              title={svc.description}
                              onClick={(e) => e.stopPropagation()}
                              className="text-yellow-500/80 hover:text-yellow-400 cursor-help flex-shrink-0"
                              data-testid={`addon-info-icon-${idx}`}
                            >
                              <Info className="h-4 w-4" />
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600 mt-2 ml-6">
                          <Clock className="h-3 w-3" />
                          <span>{a.addon_start_time} – {etTime(a.addon_end_time)}</span>
                        </div>
                        {svc.description && (
                          <p className="text-xs text-gray-500 mt-1 ml-6 line-clamp-2">{svc.description}</p>
                        )}
                        {svc.info_url && (
                          <a
                            href={svc.info_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-xs text-yellow-500 hover:text-yellow-400 mt-1.5 ml-6 font-medium"
                            data-testid={`addon-info-link-${idx}`}
                          >
                            Mehr erfahren <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        {svc.original_price > 0 && a.savings > 0 && (
                          <div className="text-xs text-gray-400 line-through">
                            CHF {svc.original_price.toFixed(2)}
                          </div>
                        )}
                        <div className="text-xl font-bold text-green-600">
                          CHF {(a.combo_price ?? 0).toFixed(2)}
                        </div>
                        {a.savings > 0 && (
                          <div className="text-xs text-green-600 font-medium">
                            -CHF {a.savings.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Einverständnis-Erklärung (Kryo/EMS) für das gewählte Add-on */}
        {consentForm && addons[selectedIdx]?.consent_form_id && (
          <div className="p-4 rounded-lg space-y-3 bg-zinc-900 border border-white/10 max-h-[40vh] overflow-y-auto" data-testid="addon-consent-section">
            <p className="font-semibold text-white text-sm">Einverständnis-Erklärung: {consentForm.title}</p>
            <ConsentSignSection form={consentForm} onChange={setConsentPayload} />
          </div>
        )}

        <DialogFooter className="flex gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="addon-decline-btn">
            {et('Nein, danke')}
          </Button>
          {addons.length > 0 && (
            <Button
              onClick={bookSelectedAddon}
              disabled={booking || (!!addons[selectedIdx]?.consent_form_id && !!consentForm && !consentPayload)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              data-testid="addon-confirm-btn"
            >
              {booking ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{et('Buchen...')}</>
              ) : (
                <><Check className="h-4 w-4 mr-2" />{et('Ja, hinzufügen')}</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddonServicePopup;
